import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import {
  UNASSIGNED_LOCATION,
  useCustomerScopeStore,
  type CustomerLocationSelection,
} from '@/stores/customer-scope.store';

const ALL_LOCATIONS_LABEL = 'All locations';
const UNASSIGNED_LABEL = 'Unassigned';

/**
 * D-130 Phase 6C — location context.
 *
 * Two renderings of one idea, chosen by the scope the SERVER reported:
 *
 *   LOCATION      a static chip naming the contact's site. No dropdown, no
 *                 "All locations", no hint that anything else exists — the
 *                 server would refuse it anyway, and offering it would be a
 *                 lie about what this person can do.
 *   ORGANIZATION  a real selector whose options are exactly `/me.locations`,
 *                 plus All locations and the explicit Unassigned bucket. The
 *                 selection is sent to the server as `locationId`; nothing is
 *                 filtered in the browser.
 *
 * Renders nothing at all until `/me` has told us the scope: guessing a default
 * would mean rendering a control the identity may not be entitled to.
 */
export function CustomerLocationContext({ className = '' }: { className?: string }) {
  const user = useCustomerAuthStore((s) => s.user);
  const selected = useCustomerScopeStore((s) => s.selectedLocationId);
  const setSelected = useCustomerScopeStore((s) => s.setSelectedLocation);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;

  // ── LOCATION: context, not choice ────────────────────────────────────────
  if (user.locationScope !== 'ORGANIZATION') {
    const name = user.location?.name;
    if (!name) return null;
    return (
      <span
        className={`inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-customer-border bg-customer-surface-muted px-2.5 py-1 text-xs font-medium text-customer-text-secondary ${className}`}
        title={`You are viewing ${name}`}
      >
        <FontAwesomeIcon
          icon={faLocationDot}
          className="h-3 w-3 shrink-0 text-customer-accent"
          aria-hidden="true"
        />
        <span className="sr-only">Your location:</span>
        <span className="truncate">{name}</span>
      </span>
    );
  }

  // ── ORGANIZATION: a real, server-backed selector ─────────────────────────
  const locations = user.locations ?? [];
  const options: Array<{ value: CustomerLocationSelection; label: string }> = [
    { value: null, label: ALL_LOCATIONS_LABEL },
    ...locations.map((l) => ({ value: l.id as CustomerLocationSelection, label: l.name })),
    { value: UNASSIGNED_LOCATION, label: UNASSIGNED_LABEL },
  ];
  const activeLabel = options.find((o) => o.value === selected)?.label ?? ALL_LOCATIONS_LABEL;

  return (
    <div ref={wrapRef} className={`relative min-w-0 ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Location filter: ${activeLabel}`}
        className="customer-pressable inline-flex min-h-11 w-full min-w-0 max-w-full items-center gap-1.5 rounded-customer-sm border border-customer-border bg-customer-surface px-2.5 text-xs font-medium text-customer-text transition-colors hover:border-customer-action/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring sm:text-sm"
      >
        <FontAwesomeIcon
          icon={faLocationDot}
          className="h-3.5 w-3.5 shrink-0 text-customer-accent"
          aria-hidden="true"
        />
        <span className="truncate">{activeLabel}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className="h-3 w-3 shrink-0 text-customer-text-secondary"
          aria-hidden="true"
        />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Location"
          className="customer-modal-in absolute left-0 z-50 mt-1.5 max-h-72 w-[min(16rem,calc(100vw-2rem))] overflow-auto rounded-customer-lg border border-customer-border bg-customer-surface p-1 shadow-customer-lg"
        >
          {options.map((opt) => {
            const active = opt.value === selected;
            return (
              <li key={opt.value ?? 'all'}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    setSelected(opt.value);
                    setOpen(false);
                    buttonRef.current?.focus();
                  }}
                  className={`customer-pressable flex min-h-11 w-full items-center gap-2 rounded-customer-sm px-2.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-customer-ring ${
                    active
                      ? 'bg-customer-surface-raised font-semibold text-customer-text'
                      : 'text-customer-text-secondary hover:bg-customer-surface-muted'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={faCheck}
                    className={`h-3 w-3 shrink-0 ${active ? 'text-customer-action-text' : 'text-transparent'}`}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 break-words">{opt.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
